
import { useState, useEffect } from "react";
import { Target, BedDouble, Sun, Clock, Wand2, Loader2, Info, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getAIGoals } from "@/lib/actions";
import type { SleepGoal, Child, SleepEvent } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { UpgradePrompt } from "@/components/upgrade-prompt";
import { GoalsTimeline } from "./goals-timeline";

type GoalsCardProps = {
  child: Child;
  goal?: SleepGoal;
  onGoalChange: (newGoal: Omit<SleepGoal, 'id' | 'userId' | 'childId' | 'reminders'>) => void;
  events: SleepEvent[];
  isSubscribed: boolean;
};

export function GoalsCard({ child, goal, onGoalChange, events, isSubscribed }: GoalsCardProps) {
  const [preferences, setPreferences] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Manual goal editing state
  const [manualBedtime, setManualBedtime] = useState(goal?.bedtime || "19:30");
  const [manualWakeUp, setManualWakeUp] = useState(goal?.wakeUpTime || "07:00");
  const [manualNightSleep, setManualNightSleep] = useState(goal?.totalNightSleep || 11);
  const [manualNaps, setManualNaps] = useState(goal?.numNaps !== undefined ? goal.numNaps : 2);
  const [manualAwakeWindow, setManualAwakeWindow] = useState(goal?.maxAwakeWindow || 2.5);
  const [manualTotalSleep, setManualTotalSleep] = useState(goal?.totalSleep24h || 13.5);

  useEffect(() => {
    if (goal) {
      setManualBedtime(goal.bedtime);
      setManualWakeUp(goal.wakeUpTime);
      setManualNightSleep(goal.totalNightSleep);
      setManualNaps(goal.numNaps);
      setManualAwakeWindow(goal.maxAwakeWindow);
      setManualTotalSleep(goal.totalSleep24h);
    }
  }, [goal]);

  const { toast } = useToast();

  // Free users can set goals once with AI, but can manually edit schedule anytime
  const canUseAI = isSubscribed || !goal;

  const handleSaveManualGoals = () => {
    onGoalChange({
      bedtime: manualBedtime,
      wakeUpTime: manualWakeUp,
      totalNightSleep: Number(manualNightSleep),
      numNaps: Number(manualNaps),
      maxAwakeWindow: Number(manualAwakeWindow),
      totalSleep24h: Number(manualTotalSleep),
    });
    setIsEditDialogOpen(false);
    toast({
      title: "Schedule Updated",
      description: "Sleep goals have been saved successfully.",
    });
  };

  const handleAIGoalGeneration = async () => {
    if (!canUseAI) return;
    setError(null);
    setIsLoading(true);

    const serializableEvents = events.map(e => ({
      ...e,
      time: e.time.toDate().toISOString(),
    }));

    const response = await getAIGoals(child, serializableEvents, preferences);

    if (response.success && response.data) {
      onGoalChange({
        bedtime: response.data.bedtime,
        wakeUpTime: response.data.wakeUpTime,
        totalNightSleep: response.data.totalNightSleep,
        numNaps: response.data.numNaps,
        maxAwakeWindow: response.data.maxAwakeWindow,
        totalSleep24h: response.data.totalSleep24h,
      });
      toast({
        title: "Goals Generated",
        description: "New AI-powered sleep goals have been set.",
      })
    } else {
      // Sanitize errors from GenKit: do not expose internal details
      const safeError = "Sorry, the AI couldn't generate goals right now. Please try again.";
      setError(safeError);
      toast({
        variant: "destructive",
        title: "Goal Generation Unavailable",
        description: "Please try again in a moment.",
      })
    }

    setIsLoading(false);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-medium">Sleep Goals</CardTitle>
            <CardDescription>Target sleep schedule for {child.name}.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {goal && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit Schedule
              </Button>
            )}
            <Target className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {goal ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BedDouble className="h-5 w-5 text-accent" />
                <span className="text-sm">Bedtime</span>
              </div>
              <span className="font-medium">{goal.bedtime}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sun className="h-5 w-5 text-accent" />
                <span className="text-sm">Wake-up</span>
              </div>
              <span className="font-medium">{goal.wakeUpTime}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-accent" />
                <span className="text-sm">Total Night Sleep</span>
              </div>
              <span className="font-medium">{goal.totalNightSleep.toFixed(1)} hours</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info className="h-5 w-5 text-accent" />
                <span className="text-sm">Day Naps</span>
              </div>
              <span className="font-medium">{goal.numNaps} nap(s)</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info className="h-5 w-5 text-accent" />
                <span className="text-sm">Awake Window</span>
              </div>
              <span className="font-medium">{goal.maxAwakeWindow.toFixed(1)} hours</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-accent" />
                <span className="text-sm">Total 24h Sleep</span>
              </div>
              <span className="font-medium">{goal.totalSleep24h.toFixed(1)} hours</span>
            </div>
            <div className="mt-6 pt-6 border-t">
              <GoalsTimeline goal={goal} />
            </div>
          </div>
        ) : (
          <div className="text-center text-sm text-muted-foreground p-4 bg-muted/50 rounded-lg">
            <p>No sleep goals have been set for {child.name} yet.</p>
            <p className="mt-2">Use the generator below to create a personalized sleep plan.</p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-4 items-start">
        <div className="w-full">
          <Label htmlFor="preferences" className="text-xs font-semibold">Parent Preferences (Optional)</Label>
          <Textarea
            id="preferences"
            placeholder="e.g., 'We prefer an earlier bedtime.' or 'We need to wake up by 7am for school.'"
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            rows={3}
            className="mt-1"
            disabled={!canUseAI}
          />
        </div>
        {!isSubscribed && goal && (
          <div className="mb-3">
            <UpgradePrompt message="Unlimited AI schedule updates for £4.99/mo" />
          </div>
        )}
        <Button onClick={handleAIGoalGeneration} disabled={isLoading || !canUseAI} className="w-full">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Wand2 className="mr-2 h-4 w-4" />
              {goal ? 'Update with AI' : 'Generate with AI'}
            </>
          )}
        </Button>
      </CardFooter>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Sleep Schedule</DialogTitle>
            <DialogDescription>
              Adjust target sleep times and awake windows for {child.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-bedtime" className="text-xs font-medium">Bedtime (HH:mm)</Label>
                <Input
                  id="edit-bedtime"
                  value={manualBedtime}
                  onChange={(e) => setManualBedtime(e.target.value)}
                  placeholder="19:30"
                />
              </div>
              <div>
                <Label htmlFor="edit-wakeup" className="text-xs font-medium">Wake-up (HH:mm)</Label>
                <Input
                  id="edit-wakeup"
                  value={manualWakeUp}
                  onChange={(e) => setManualWakeUp(e.target.value)}
                  placeholder="07:00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-night" className="text-xs font-medium">Night Sleep (hrs)</Label>
                <Input
                  id="edit-night"
                  type="number"
                  step="0.5"
                  value={manualNightSleep}
                  onChange={(e) => setManualNightSleep(Number(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="edit-naps" className="text-xs font-medium">Day Naps</Label>
                <Input
                  id="edit-naps"
                  type="number"
                  step="1"
                  value={manualNaps}
                  onChange={(e) => setManualNaps(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-awake" className="text-xs font-medium">Max Awake Window (hrs)</Label>
                <Input
                  id="edit-awake"
                  type="number"
                  step="0.25"
                  value={manualAwakeWindow}
                  onChange={(e) => setManualAwakeWindow(Number(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="edit-total" className="text-xs font-medium">Total 24h Sleep (hrs)</Label>
                <Input
                  id="edit-total"
                  type="number"
                  step="0.5"
                  value={manualTotalSleep}
                  onChange={(e) => setManualTotalSleep(Number(e.target.value))}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveManualGoals}>Save Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
